import { observer } from 'mobx-react'
import React, { ReactElement, useEffect } from 'react'
import { Redirect } from 'react-router-dom'

import {
   API_SUCCESS,
   APIStatus,
   API_FETCHING,
   isAPIFetching
} from '@flowwai-frontend/shared/api-common'
import { ACCESS_DENIED_ROUTE_PATH } from '@flowwai-frontend/bps-officer-web/common/data-access'
import { LoadingWrapper } from '@flowwai-frontend/shared/common-components'
import { getLoadingStatus } from '@flowwai-frontend/shared/utils/common'
import { UserAttendanceStatus } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import { useGetSearchAndMonitoringConfig } from '@flowwai-frontend/bps-officer-web/search-and-monitoring/data-access'

import { useGetCurrentUserDetails } from '../hooks/apis/useGetCurrentUserDetails'
import { useGetUserAppConfig } from '../hooks/apis/useGetUserAppConfig'
import { useUserAppConfigStore } from '../hooks/stores/useUserAppConfigStore'
import useUserAttendanceStatus from '../hooks/useUserAttendanceStatus'
import { useUserAttendanceUIStore } from '../hooks/useUserAttendanceUIStore'

interface CurrentUserDetailsProps {
   loadingWrapperContainerCss?: React.CSSProperties
}

const withCurrentUserDetails = (Component: (_: any) => ReactElement) => {
   //FIXME:Add Appropriate Types for Component
   const CurrentUserDetails = observer((props: CurrentUserDetailsProps) => {
      const { loadingWrapperContainerCss } = props
      const userAttendanceUIStore = useUserAttendanceUIStore()

      const { getAttendanceStatus } = useUserAttendanceStatus()

      const {
         getUserProfileAPIError,
         getUserProfileAPIStatus,
         getUserProfileAPITrigger
      } = useGetCurrentUserDetails()

      const {
         getUserAppConfigAPITrigger,
         getUserAppConfigAPIError,
         getUserAppConfigAPIStatus
      } = useGetUserAppConfig()

      const getSearchAndMonitoringConfigAPI = useGetSearchAndMonitoringConfig()

      const userAppConfigStore = useUserAppConfigStore()
      const { permissions, userDetails } = userAppConfigStore
      const attendanceStatus = userDetails
         ? getAttendanceStatus(userDetails)
         : undefined

      const doNetworkCall = () => {
         if (!userAppConfigStore.userDetails) getUserProfileAPITrigger()
         if (!userAppConfigStore.salesPipelinesIds) getUserAppConfigAPITrigger()
         getSearchAndMonitoringConfigAPI.triggerAPI({})
      }

      const onRetry = () => {
         if (getUserProfileAPIError) getUserProfileAPITrigger()
         else if (getUserAppConfigAPIError) getUserAppConfigAPITrigger()
         else if (getSearchAndMonitoringConfigAPI.apiError)
            getSearchAndMonitoringConfigAPI.triggerAPI({})
      }

      useEffect(() => {
         doNetworkCall()
      }, [])

      useEffect(() => {
         const shouldBlockUser =
            permissions.isAttendanceTrackingEnabled &&
            attendanceStatus === UserAttendanceStatus.Offline
         if (shouldBlockUser) {
            userAttendanceUIStore.setIsClockInModalOpen()
         }
      }, [permissions.isAttendanceTrackingEnabled, attendanceStatus])

      const getAPIStatus = (): APIStatus => {
         const haveCurrentUserDetails =
            userAppConfigStore.userDetails &&
            userAppConfigStore.salesPipelinesIds

         return haveCurrentUserDetails
            ? API_SUCCESS
            : getLoadingStatus(
                 getUserProfileAPIStatus,
                 getUserAppConfigAPIStatus
              )
      }

      const isSearchAndMonitoringAPIFetching = isAPIFetching(
         getSearchAndMonitoringConfigAPI.apiStatus
      )

      return (
         <LoadingWrapper
            apiStatus={
               isSearchAndMonitoringAPIFetching ? API_FETCHING : getAPIStatus()
            }
            apiError={getUserProfileAPIError || getUserAppConfigAPIError}
            onRetry={onRetry}
            containerCss={loadingWrapperContainerCss}
         >
            {userAppConfigStore.permissions?.hasAccessForWebApp ? (
               <Component props={props} />
            ) : (
               <Redirect to={ACCESS_DENIED_ROUTE_PATH} />
            )}
         </LoadingWrapper>
      )
   })

   return () => <CurrentUserDetails />
}

export { withCurrentUserDetails }
